import React, { useState, useEffect, useRef } from "react";
import {
  XMarkIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { institutionAPI } from "../../../services/api";
import ManagePartnershipType from "./ManagePartnershipType";
import ManageCollaborationType from "./ManageCollaborationType";

const initialState = {
  institution_id: "",
  partnership_name: "",
  partnership_type_id: "",
  collaboration_type: "",
  start_date: "",
  end_date: "",
  remarks: "",
};

export default function AddPartnership({ isOpen, onClose, onSave }) {
  const [formData, setFormData] = useState(initialState);
  const [institutions, setInstitutions] = useState([]);
  const [partnershipTypes, setPartnershipTypes] = useState([]);
  const [collaborationTypes, setCollaborationTypes] = useState([]);
  const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
  const [isCollabModalOpen, setIsCollabModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);

  const [dropdownOpen, setDropdownOpen] = useState({
    institution: false,
    partnershipType: false,
    collaborationType: false,
  });

  // ---------------- FETCH DROPDOWNS ----------------
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const [instRes, typeRes, collabRes] = await Promise.all([
          institutionAPI.getInstitutions({ per_page: "50" }),
          institutionAPI.getPartnerTypes({ per_page: 50 }),
          institutionAPI.getCollaborationTypes({ per_page: 50 }),
        ]);

        if (instRes.data.success) {
          setInstitutions(
            instRes.data.data.institutions.map((i) => ({
              id: i.institution_id.toString(),
              name: i.name,
            }))
          );
        }

        if (typeRes.data.success) {
          setPartnershipTypes(
            typeRes.data.data?.partner_types?.map((t) => ({
              id: t.id.toString(),
              name: t.name,
            })) || []
          );
        }

        if (collabRes.data.success) {
          setCollaborationTypes(
            collabRes.data.data?.collaboration_types?.map((c) => ({
              id: c.id.toString(),
              name: c.name,
            })) || []
          );
        }
      } catch (err) {
        console.error("Dropdown fetch error:", err);
      }
    };

    if (isOpen) fetchDropdowns();
  }, [isOpen]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setDropdownOpen({
          institution: false,
          partnershipType: false,
          collaborationType: false,
        });
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const resetForm = () => setFormData(initialState);

  // ---------------- SUBMIT ----------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    // ---------------- VALIDATION ----------------
    if (!formData.institution_id) {
      alert("Please select an Institution.");
      return;
    }
    if (!formData.partnership_name.trim()) {
      alert("Please enter a Partnership Name.");
      return;
    }
    if (!formData.partnership_type_id) {
      alert("Please select a Partnership Type.");
      return;
    }
    if (!formData.collaboration_type) {
      alert("Please select a Collaboration Type.");
      return;
    }
    if (!formData.start_date || !formData.end_date) {
      alert("Please select Start and End dates.");
      return;
    }
    if (new Date(formData.end_date) < new Date(formData.start_date)) {
      alert("End date cannot be earlier than start date.");
      return;
    }

    const collab = collaborationTypes.find(
      (c) => c.id === formData.collaboration_type
    );

    setLoading(true);

    try {
      const payload = {
        institution_id: Number(formData.institution_id),
        partner_name: formData.partnership_name.trim(),
        partner_type_id: Number(formData.partnership_type_id), 
        status: "active",
        agreement_title: collab.name,
        collaboration_type_id: Number(formData.collaboration_type),
        agreement_date: formData.start_date,
        start_date: formData.start_date,
        end_date: formData.end_date,
        remarks: formData.remarks.trim(),
      };

      const res = await institutionAPI.createPartnership(payload);

      if (res.data?.success) {
        const p = res.data.data.partnership;

        onSave({
          partnership_id: p.partnership_id,
          partner_name: p.partner_name,
          agreement_title: p.agreement_title,
          agreement_date: p.agreement_date,
          start_date: p.start_date,
          end_date: p.end_date,
          remarks: p.remarks,
          status: p.status,

          institution: {
            institution_id: p.institution?.institution_id,
            name: p.institution?.name || "N/A",
          },

          partner_type: {
            id: p.partner_type?.id || Number(formData.partnership_type_id),
            name:
              p.partner_type?.name ||
              partnershipTypes.find(t => t.id === formData.partnership_type_id)?.name ||
              "N/A",
          },
        });

        resetForm();
        onClose();
      }
    } catch (err) {
      console.error("Create partnership error:", err.response?.data || err);
      alert(err.response?.data?.message || "Failed to create partnership");
    } finally {
      setLoading(false);
    }
  };

  // ---------------- SELECT RENDER ----------------
  const renderSelect = (name, value, options, key) => (
    <div className="relative w-full">
      <select
        name={name}
        value={value}
        required
        onChange={handleChange}
        onClick={() => setDropdownOpen((p) => ({ ...p, [key]: !p[key] }))}
        className="w-full border rounded-md px-3 py-2 pr-10 appearance-none focus:ring-1 focus:ring-blue-500"
      >
        <option value="">Select</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
        {dropdownOpen[key] ? (
          <ChevronDownIcon className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronUpIcon className="w-5 h-5 text-gray-400" />
        )}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>

      <div
        ref={wrapperRef}
        className="relative bg-white rounded-lg w-full max-w-4xl shadow-lg p-6"
      >
        <div className="flex justify-between border-b pb-2 mb-4">
          <h2 className="text-xl font-semibold text-gray-800">
            Add New Partnership
          </h2>
          <button onClick={onClose}>
            <XMarkIcon className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Row 1 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Institution <span className="text-red-500">*</span>
              </label>
              {renderSelect("institution_id", formData.institution_id, institutions, "institution")}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Partnership Name <span className="text-red-500">*</span>
              </label>
              <input
                name="partnership_name"
                value={formData.partnership_name}
                onChange={handleChange}
                className="w-full border rounded-md px-3 py-2"
                required
              />
            </div>
          </div>

          {/* Row 2 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700">
                  Partnership Type <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setIsTypeModalOpen(true)}
                  className="ml-2 px-3 rounded text-xs text-blue-700 bg-blue-50 hover:bg-blue-100 transition whitespace-nowrap"
                >
                  + New Partnership Type
                </button>
              </div>
              <div className="mt-1">
                {renderSelect(
                  "partnership_type_id",
                  formData.partnership_type_id,
                  partnershipTypes,
                  "partnershipType"
                )}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700">
                  Type of Collaboration <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setIsCollabModalOpen(true)}
                  className="ml-2 px-3 rounded text-xs text-blue-700 bg-blue-50 hover:bg-blue-100 transition whitespace-nowrap"
                >
                  + New Collaboration Type
                </button>
              </div>
              <div className="mt-1">
                {renderSelect(
                  "collaboration_type",
                  formData.collaboration_type,
                  collaborationTypes,
                  "collaborationType"
                )}
              </div>
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleChange}
                required
                className="w-full border rounded-md px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="end_date"
                value={formData.end_date}
                onChange={handleChange}
                required
                className="w-full border rounded-md px-3 py-2"
              />
            </div>
          </div>

          {/* Remarks */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Remarks
            </label>
            <textarea
              name="remarks"
              value={formData.remarks}
              onChange={handleChange}
              rows="3"
              className="w-full border rounded-md px-3 py-2"
            />
          </div>

          <div className="flex justify-end pt-3 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 rounded-md mr-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2 bg-blue-600 text-white rounded-md"
            >
              {loading ? "Saving..." : "Add"}
            </button>
          </div>
        </form>
      </div>

      <ManageCollaborationType
        isOpen={isCollabModalOpen}
        onClose={() => setIsCollabModalOpen(false)}
        onRefresh={() => {
          institutionAPI.getCollaborationTypes({ per_page: 50 }).then((res) => {
            if (res.data.success) {
              setCollaborationTypes(
                res.data.data?.collaboration_types?.map((c) => ({
                  id: c.id.toString(),
                  name: c.name,
                })) || []
              );
            }
          });
        }}
      />

      <ManagePartnershipType
        isOpen={isTypeModalOpen}
        onClose={() => setIsTypeModalOpen(false)}
        onRefresh={() => {
          institutionAPI.getPartnerTypes({ per_page: 50 }).then((res) => {
            if (res.data.success) {
              setPartnershipTypes(
                res.data.data?.partner_types?.map((t) => ({
                  id: t.id.toString(),
                  name: t.name,
                })) || []
              );
            }
          });
        }}
      />
    </div>
  );
}
